import { useEffect, useState } from "react";
import BadTokenComponent from "./error-pages/bad-token.component";
import ExpiredTokenComponent from "./error-pages/expired-token.component";
import SignUpFormV2Component from "./sign-up-form/sign-up-form-v2.component";
import { SignUpFormContextProvider } from "./sign-up-form/sign-up-form.context";
import WelcomeMessageComponent from "./welcome-message/welcome-message.component";
import styles from "./sign-up-v4.styles.module.scss";
import { useAuthInit } from "../../hooks/use-auth-init.hook";
import { navigateToUrl } from "single-spa";
import QuestionnaireExpiredTokenComponent from "./error-pages/questionnaire-expired-token.component";
import { SignupTokenType } from "./sign-up-v4.constants";
import { useSignupToken } from "./hooks/use-signup-token.hook";
import FreeTrialExpiredTokenComponent from "./error-pages/freetrial-expired-token.component";
import { useRedirect } from "../../hooks/use-redirect.hook";

enum TokenError {
  QUERY_PARAM_ISSUE = "QUERY_PARAM_ISSUE",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  UNKNOWN = "UNKNOWN",
}

const SignUpV4Component = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tokenError, setTokenError] = useState<TokenError | "">("");
  const [isTokenValidated, setIsTokenValidated] = useState<boolean>(false);
  const { signupTokenType, token } = useSignupToken();
  const { isAuthInitialized, isAuthInitSuccess } = useAuthInit();
  const { redirect } = useRedirect();

  useEffect(() => {
    if (isTokenValidated) {
      return;
    }
    if (!isAuthInitialized) {
      return;
    }
    if (isAuthInitialized && isAuthInitSuccess) {
      void (async () => {
        await window.eventBus.publish("WS_CONNECT");
        await window.eventBus.publish("SESSION_EVENT_LOAD");
        await window.eventBus.publish("USER_STATE_LOAD");
        setIsLoading(false);
        navigateToUrl(redirect);
      })();
      return;
    }
    if (!token) {
      setTokenError(TokenError.QUERY_PARAM_ISSUE);
      setIsLoading(false);
      return;
    }
    void (async () => {
      try {
        const response = await fetch(`${window.gxApiConnection}/api/v4/auth/validate-signup-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        if (!response.ok) {
          const responseJson = await response.json();

          setTokenError(responseJson.message || TokenError.UNKNOWN);
        }
      } catch (error) {
        setTokenError(TokenError.UNKNOWN);
      } finally {
        setIsTokenValidated(true);
        setIsLoading(false);
      }
    })();
  }, [isAuthInitSuccess, isAuthInitialized, isTokenValidated, token, redirect]);

  return (
    <div className={styles.root}>
      {!isLoading && (
        <div className={styles.contentWrapper}>
          <div role="complementary" className={styles.leftSection}>
            {!tokenError && <WelcomeMessageComponent />}
            {tokenError && tokenError === TokenError.TOKEN_EXPIRED && signupTokenType === SignupTokenType.INVITE_SURVEY && <QuestionnaireExpiredTokenComponent />}
            {tokenError && tokenError === TokenError.TOKEN_EXPIRED && signupTokenType === SignupTokenType.INVITE_INITIATIVE && <ExpiredTokenComponent />}
            {tokenError && tokenError === TokenError.TOKEN_EXPIRED && signupTokenType === SignupTokenType.FREE_TRIAL && <FreeTrialExpiredTokenComponent />}
            {tokenError && tokenError !== TokenError.TOKEN_EXPIRED && <BadTokenComponent />}
          </div>
          <section className={styles.rightSection}>
            <SignUpFormContextProvider>
              {!tokenError && <SignUpFormV2Component />}
            </SignUpFormContextProvider>
          </section>
        </div>
      )}
      <div className={styles.triangle} />
    </div>
  );
};

export default SignUpV4Component;
